import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Logo, TreeMark } from './Logo'
import { Pill } from './marketing/primitives'

/**
 * Split-screen auth shell: an editorial forest brand panel beside the form.
 * Used by Login / Onboarding / Paywall so the whole sign-on experience is consistent.
 */
export function AuthLayout({
  eyebrow,
  headline,
  points,
  footnote = '$20 / seat · first month free · cancel anytime',
  children,
}: {
  eyebrow: string
  headline: ReactNode
  points?: string[]
  /** Brand-panel footer line; pass null to omit (e.g. invite screens — no purchase context). */
  footnote?: string | null
  children: ReactNode
}) {
  return (
    <div className="grid min-h-full md:grid-cols-2">
      {/* Brand panel */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-forest p-12 text-cream md:flex">
        <Link to="/" className="relative"><Logo className="text-xl text-cream" /></Link>

        <div className="relative">
          <div className="mb-8 flex">
            <span className="animate-float grid h-20 w-20 place-items-center rounded-full bg-cream/10">
              <TreeMark className="h-12 w-12" />
            </span>
          </div>
          <div className="inline-flex"><Pill>{eyebrow}</Pill></div>
          <h2 className="mt-5 max-w-sm font-display text-4xl leading-tight">{headline}</h2>
          {points && (
            <ul className="mt-8 space-y-3">
              {points.map((p) => (
                <li key={p} className="flex items-center gap-3 text-cream/85">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-cream/15">
                    <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                      <path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="label-mono relative text-cream/50">{footnote ?? ' '}</p>
      </aside>

      {/* Content panel — settles in with the shared page rise so entering auth never hard-cuts. */}
      <main className="grid place-items-center px-6 py-16">
        <div className="page-enter w-full max-w-md">{children}</div>
      </main>
    </div>
  )
}
